/* eslint-disable */
import { useState, useEffect, useRef, useCallback } from 'react';
import { motion } from 'framer-motion';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import TripPlan from './TripPlan';

// Fallbacks mirror App.js so the component also works standalone.
const DEFAULT_API_BASE =
  process.env.REACT_APP_API_URL ||
  'https://gxqbqpargppbzwceamea.supabase.co/functions/v1/db-proxy';
const SUPABASE_ANON_KEY = process.env.REACT_APP_SUPABASE_ANON_KEY || '';
const CARTO_KEY = process.env.REACT_APP_CARTO_KEY || '';
const defaultApiFetch = (url) =>
  fetch(url, { headers: { Authorization: 'Bearer ' + SUPABASE_ANON_KEY } });

// Depth colours: green (direct) → amber (+1) → orange (+2 and beyond).
const PALETTE = ['#15803D', '#D97706', '#EA580C'];
const colorAt = (d) => PALETTE[Math.min(d, PALETTE.length - 1)];
// Lighter variants for intermediate-stop pins.
const LIGHT = ['#86EFAC', '#FCD34D', '#FDBA74'];
const lightAt = (d) => LIGHT[Math.min(d, LIGHT.length - 1)];

// Brand coral for route polylines.
const ROUTE_COLOR = '#E8634A';

const mins = (sec) => (sec != null ? Math.round(sec / 60) : null);

// DB timetable times come as YYMMDDHHmm → HH:mm.
const fmtTime = (pt) => { if (!pt) return '··:··'; const s = String(pt); return `${s.slice(6, 8)}:${s.slice(8, 10)}`; };

// TripAdvisor link for a destination (no internal attractions API yet).
const taUrl = (name) => {
  const shortName = String(name || '').replace(/ (Hbf|Bahnhof|Hauptbahnhof).*/, '').trim();
  return `https://www.tripadvisor.com/Search?q=${encodeURIComponent(shortName + ' Germany')}`;
};

const makeIcon = (color, size) =>
  L.divIcon({
    className: '',
    html: `<div style="width:${size}px;height:${size}px;border-radius:50%;background:${color};border:2.5px solid white;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });

// Filter matching: route_short_name starts with RE/RB/S + digit.
const PREFIX = { RE: /^RE\s*\d/i, RB: /^RB\s*\d/i, S: /^S\s*\d/i };
const matchesFilters = (route, filters) => {
  if (!route) return false;
  const name = String(route).trim();
  return Object.keys(filters).some((k) => filters[k] && PREFIX[k].test(name));
};
const catOf = (route) => {
  const n = String(route || '').trim();
  if (/^RE/i.test(n)) return 're';
  if (/^RB/i.test(n)) return 'rb';
  if (/^S/i.test(n)) return 's';
  return 'other';
};

const llOf = (o) => (o && o.stop_lat != null && o.stop_lon != null ? [o.stop_lat, o.stop_lon] : null);

// Mode tiles (Tagesausflug active), matching the app's mode set + ids.
const MODES = [
  { id: 'soweit', icon: '→', label: 'So weit', desc: 'How far can you go?' },
  { id: 'zuzweit', icon: '↔', label: 'Zu zweit', desc: 'Find a meeting point' },
  { id: 'tagesausflug', icon: '☀', label: 'Tagesausflug', desc: 'Day trips from your station', active: true },
  { id: 'entlang', icon: '◎', label: 'Entlang', desc: 'Stops along your route', soon: true },
];

// Scoped styles (gx- prefix) so nothing collides with App.css (.card/.badge/.logo).
const STYLES = `
.gx-root {
  --bg:#F5F3EF; --surface:#FFFFFF; --surf2:#F3F1ED; --surf3:#ECEAE5;
  --ink:#1C1C1A; --muted:#6B6B67; --faint:#A8A8A4;
  --bdr:rgba(28,28,26,.1); --bdr2:rgba(28,28,26,.18);
  --accent:#D4521A; --accent-lt:#FBE9E0;
  --re-bg:#E8EDF6; --re-fg:#1A3A6E;
  --rb-bg:#E8F2EC; --rb-fg:#2D6A3F;
  --s-bg:#F2E8F5;  --s-fg:#7B2D8A;
  position: fixed; inset: 0;
  font-family: 'DM Sans', system-ui, sans-serif; color: var(--ink);
}

.gx-map { position: absolute; inset: 0; z-index: 0; }
.gx-root .leaflet-top.leaflet-left { left: auto; right: 0; }
.gx-root path.leaflet-interactive { transition: stroke-opacity .15s, stroke-width .15s; }

.gx-panel {
  position: absolute; top: 16px; left: 16px; z-index: 1000;
  width: 320px; max-height: calc(100vh - 32px); overflow-y: auto;
  display: flex; flex-direction: column; gap: 10px;
  padding: 14px 16px;
  background: var(--surface); border: 1px solid var(--bdr);
  border-radius: 16px; box-shadow: 0 6px 24px rgba(0,0,0,.12);
}

/* ── mobile (≤640px) chrome: floating top trip bar + bottom-sheet carousel ── */
.gxm-top {
  position: absolute; top: 0; left: 0; right: 0; z-index: 1000;
  display: flex; align-items: flex-start; gap: 8px; padding: 10px 12px;
  background: linear-gradient(to bottom, rgba(245,243,239,.92), rgba(245,243,239,0));
}
.gxm-back { flex: 0 0 auto; width: 30px; height: 30px; border-radius: 50%; border: none; background: #fff; color: var(--ink); font-size: 18px; line-height: 1; cursor: pointer; box-shadow: 0 2px 8px rgba(0,0,0,.15); display: flex; align-items: center; justify-content: center; }
.gxm-pill { flex: 1; min-width: 0; background: #fff; border-radius: 16px; box-shadow: 0 2px 10px rgba(0,0,0,.12); overflow: hidden; }
.gxm-pill-head { display: flex; align-items: center; gap: 8px; width: 100%; padding: 9px 12px; background: none; border: none; cursor: pointer; font-family: 'DM Sans', sans-serif; text-align: left; }
.gxm-origin-dot { flex: 0 0 auto; width: 9px; height: 9px; border-radius: 50%; background: #1A1A1A; }
.gxm-path { flex: 1; min-width: 0; font-size: 13px; font-weight: 600; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gxm-chev { flex: 0 0 auto; color: var(--faint); font-size: 12px; }
.gxm-edit { flex: 0 0 auto; color: var(--accent); font-size: 12px; font-weight: 600; }
.gxm-legs { padding: 4px 12px 10px; border-top: 1px solid var(--bdr); display: flex; flex-direction: column; gap: 6px; }
.gxm-leg { display: flex; align-items: center; gap: 8px; font-size: 12px; color: var(--muted); }
.gxm-leg-txt { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gxm-remove { align-self: flex-start; background: none; border: none; padding: 2px 0; margin-top: 2px; color: var(--accent); font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; cursor: pointer; }
.gxm-search-wrap { position: relative; padding: 6px; }
.gxm-search { width: 100%; padding: 8px 10px; font-size: 14px; font-family: 'DM Sans', sans-serif; color: var(--ink); border: 1px solid var(--bdr2); border-radius: 10px; outline: none; }
.gxm-search:focus { border-color: var(--accent); }
.gxm-suggest { position: absolute; top: calc(100% + 2px); left: 6px; right: 6px; z-index: 30; background: #fff; border: 1px solid var(--bdr2); border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,.16); overflow-y: auto; max-height: 240px; }

.gxm-sheet { position: absolute; left: 0; right: 0; bottom: 0; z-index: 1000; background: #fff; border-radius: 18px 18px 0 0; box-shadow: 0 -4px 20px rgba(0,0,0,.12); padding-bottom: 4px; }
.gxm-handle { display: flex; justify-content: center; padding: 8px 0 4px; }
.gxm-handle-pill { width: 28px; height: 3px; border-radius: 2px; background: var(--bdr2); }
.gxm-count { padding: 2px 12px 8px; font-size: 11px; font-weight: 600; text-transform: uppercase; letter-spacing: .08em; color: var(--muted); }
.gxm-carousel { display: flex; gap: 8px; padding: 0 12px 4px; overflow-x: auto; scroll-snap-type: x mandatory; -webkit-overflow-scrolling: touch; scrollbar-width: none; }
.gxm-carousel::-webkit-scrollbar { display: none; }
.gxm-card { flex: 0 0 100%; box-sizing: border-box; scroll-snap-align: center; background: var(--surf2); border: 1.5px solid transparent; border-radius: 12px; padding: 12px 13px; }
.gxm-card.active { background: #fff; border-color: var(--accent); }
.gxm-card-name { font-size: 16px; font-weight: 500; color: var(--ink); cursor: pointer; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gxm-card-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 4px; }
.gxm-card-meta { font-size: 12px; color: var(--muted); }
.gxm-card-link { flex: 0 0 auto; font-size: 12px; color: var(--accent); font-weight: 600; cursor: pointer; }
.gxm-card-inter { margin-top: 8px; border-top: 1px solid var(--bdr); padding-top: 6px; }
.gxm-card-inter-row { display: flex; justify-content: space-between; gap: 10px; padding: 3px 0; font-size: 12px; color: var(--muted); cursor: pointer; }
.gxm-dots { display: flex; justify-content: center; gap: 5px; padding: 6px 0; }
.gxm-dot { width: 5px; height: 5px; border-radius: 50%; background: var(--bdr2); }
.gxm-dot.active { background: #1C1C1A; }
.gxm-filters { display: flex; align-items: center; gap: 6px; padding: 10px 12px; border-top: 1px solid var(--bdr); }
.gxm-filters .gx-pill { font-size: 11px; padding: 4px 11px; }
.gxm-check { margin-left: auto; display: flex; align-items: center; gap: 5px; font-size: 11px; color: var(--muted); }
.gxm-check input { accent-color: var(--accent); }

.gx-nav { display: flex; align-items: center; justify-content: space-between; }
.gx-logo { font-family: 'Fraunces', serif; font-size: 20px; font-weight: 700; font-style: italic; letter-spacing: -.025em; color: var(--ink); }
.gx-nav-links { display: flex; align-items: center; gap: 2px; }
.gx-nav-link { background: none; border: none; font-family: 'DM Sans', sans-serif; font-size: 12px; color: var(--muted); cursor: pointer; padding: 4px 6px; border-radius: 6px; text-decoration: none; }
.gx-nav-link:hover { color: var(--ink); background: var(--surf2); }

.gx-modes { display: grid; grid-template-columns: 1fr 1fr; gap: 6px; }
.gx-mode-card {
  display: flex; align-items: center; gap: 7px; min-width: 0;
  padding: 9px 11px; background: var(--surf2);
  border: 1.5px solid transparent; border-radius: 12px;
  cursor: pointer; transition: border-color .15s, background .15s;
}
.gx-mode-card:hover { background: var(--surf3); }
.gx-mode-card.active { border-color: var(--accent); background: var(--accent-lt); }
.gx-mode-icon { font-size: 16px; line-height: 1; color: var(--ink); flex: 0 0 auto; }
.gx-mode-text { display: flex; flex-direction: column; min-width: 0; }
.gx-mode-label { font-family: 'Fraunces', serif; font-size: 12px; font-weight: 600; color: var(--ink); line-height: 1.15; }
.gx-mode-desc { font-size: 10px; color: var(--muted); line-height: 1.25; }
.gx-mode-card.soon { opacity: .55; cursor: default; }
.gx-mode-card.soon:hover { background: var(--surf2); }

.gx-search-wrap { position: relative; }
.gx-search { width: 100%; padding: 11px 12px; font-size: 14px; font-weight: 500; font-family: 'DM Sans', sans-serif; color: var(--ink); background: var(--surface); border: 1px solid var(--bdr2); border-radius: 10px; outline: none; transition: border-color .15s; -webkit-appearance: none; appearance: none; }
.gx-search:focus { border-color: var(--accent); }
.gx-search::placeholder { color: var(--faint); font-weight: 400; }
.gx-suggest { position: absolute; top: calc(100% + 4px); left: 0; right: 0; z-index: 20; background: var(--surface); border: 1px solid var(--bdr2); border-radius: 10px; box-shadow: 0 8px 24px rgba(0,0,0,.14); overflow-y: auto; max-height: 240px; }
.gx-suggest-item { display: block; width: 100%; text-align: left; padding: 9px 12px; background: none; border: none; border-bottom: 1px solid var(--bdr); font-family: 'DM Sans', sans-serif; font-size: 13px; color: var(--ink); cursor: pointer; }
.gx-suggest-item:last-child { border-bottom: none; }
.gx-suggest-item:hover { background: var(--surf2); }

.gx-pills { display: flex; gap: 6px; }
.gx-pill { padding: 5px 13px; border-radius: 100px; border: 1px solid var(--bdr2); background: var(--surface); color: var(--muted); font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; cursor: pointer; transition: all .13s; }
.gx-pill.active { color: #fff; border-color: transparent; }
.gx-pill.RE.active { background: var(--re-fg); }
.gx-pill.RB.active { background: var(--rb-fg); }
.gx-pill.S.active  { background: var(--s-fg); }

.gx-check { display: flex; align-items: center; gap: 7px; font-size: 12px; color: var(--muted); cursor: pointer; }
.gx-check input { accent-color: var(--accent); }

.gx-desc { margin: 0; font-size: 12px; line-height: 1.45; color: var(--muted); }

.gx-trip { background: #FDF0EC; border: 1px solid rgba(212,82,26,.18); border-radius: 12px; padding: 11px 13px; }
.gx-trip-label { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .08em; color: var(--accent); margin-bottom: 6px; }
.gx-trip-path { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; font-size: 13px; }
.gx-trip-stop { font-family: 'DM Sans', sans-serif; font-weight: 600; color: var(--ink); }
.gx-trip-origin { cursor: pointer; }
.gx-trip-origin:hover { color: var(--accent); text-decoration: underline; }
.gx-trip-arrow { color: var(--accent); font-weight: 400; }
.gx-trip-leg { display: inline-flex; align-items: center; font-family: 'DM Sans', sans-serif; font-size: 11px; font-weight: 700; border-radius: 100px; padding: 2px 8px; }
.gx-trip-leg.re { background: var(--re-bg); color: var(--re-fg); }
.gx-trip-leg.rb { background: var(--rb-bg); color: var(--rb-fg); }
.gx-trip-leg.s  { background: var(--s-bg);  color: var(--s-fg); }
.gx-trip-leg.other { background: var(--surf3); color: var(--muted); }
.gx-trip-x { flex: 0 0 auto; margin-left: 2px; width: 18px; height: 18px; padding: 0; border: none; border-radius: 50%; background: rgba(212,82,26,.15); color: var(--accent); font-size: 13px; line-height: 1; cursor: pointer; display: inline-flex; align-items: center; justify-content: center; }
.gx-trip-x:hover { background: var(--accent); color: #fff; }

/* ── "Your trip" header + Plan CTA + accordion ── */
.gx-trip-head { display: flex; align-items: center; gap: 8px; width: 100%; padding: 0; background: none; border: none; cursor: pointer; text-align: left; font-family: 'DM Sans', sans-serif; }
.gx-trip-head .gx-trip-label { margin-bottom: 0; flex: 0 0 auto; }
.gx-trip-condensed { flex: 1; min-width: 0; font-size: 12px; font-weight: 600; color: var(--ink); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gx-trip-chev { flex: 0 0 auto; color: var(--accent); font-size: 12px; }
.gx-trip-cta { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 9px; }
.gx-trip-cta-txt { font-size: 12px; color: var(--muted); }
.gx-plan-btn { flex: 0 0 auto; background: var(--accent); color: #fff; border: none; border-radius: 100px; padding: 7px 14px; font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; cursor: pointer; }
.gx-plan-btn:hover { filter: brightness(1.05); }
.gx-trip-acc { max-height: 0; opacity: 0; overflow: hidden; transition: max-height 0.4s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease; }
.gx-trip-acc.open { max-height: 1400px; opacity: 1; }
.gx-trip-acc-inner { padding-top: 10px; margin-top: 8px; border-top: 1px solid rgba(212,82,26,.18); }

/* timeline */
.gx-tl { display: flex; flex-direction: column; gap: 2px; }
.gx-tl-leg { display: flex; align-items: center; gap: 8px; padding: 2px 0 2px 4px; }
.gx-tl-legtime { font-size: 11px; color: var(--muted); }
.gx-tl-stop { display: flex; align-items: center; gap: 8px; }
.gx-tl-dot { flex: 0 0 auto; width: 9px; height: 9px; border-radius: 50%; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,.25); }
.gx-tl-name { font-family: 'DM Sans', sans-serif; font-size: 13px; font-weight: 600; color: var(--ink); }
.gx-tl-deprow { padding: 0 0 6px 17px; }
.gx-tl-dep { font-size: 11px; color: var(--ink); font-variant-numeric: tabular-nums; }
.gx-tl-dep.load, .gx-tl-dep.none { color: var(--faint); }
.gx-tl-remove { align-self: flex-start; background: none; border: none; padding: 2px 0; margin-top: 4px; color: var(--accent); font-family: 'DM Sans', sans-serif; font-size: 12px; font-weight: 600; cursor: pointer; }

/* map dim behind the open accordion */
.gx-dim { position: absolute; inset: 0; z-index: 500; background: rgba(20,18,16,0.15); cursor: pointer; }

/* mobile trip accordion (inside the pill) */
.gxm-acc-inner { position: relative; max-height: 68vh; overflow-y: auto; padding: 10px 12px 12px; margin-top: 0; border-top: 1px solid var(--bdr); }
.gxm-acc-x { position: absolute; top: 4px; right: 6px; z-index: 2; background: none; border: none; font-size: 18px; line-height: 1; color: var(--muted); cursor: pointer; padding: 2px 6px; }
.gxm-plan { width: 100%; margin-top: 10px; }

.gx-count { font-size: 10px; font-weight: 600; text-transform: uppercase; letter-spacing: .08em; color: var(--faint); margin-bottom: 8px; }

.gx-card { background: var(--surface); border: 1.5px solid var(--bdr); border-radius: 12px; padding: 12px 13px; margin-bottom: 7px; transition: border-color .15s; }
.gx-card:hover { border-color: var(--bdr2); }
.gx-card.open { border-color: var(--ink); }
.gx-card--highlighted { border-color: #E8634A; border-left-width: 3px; background: rgba(232,99,74,.06); }
.gx-row { display: flex; align-items: center; gap: 8px; cursor: pointer; }
.gx-dot { width: 9px; height: 9px; border-radius: 50%; flex: 0 0 9px; border: 2px solid #fff; box-shadow: 0 1px 3px rgba(0,0,0,.25); }
.gx-badge { display: inline-block; font-family: 'DM Sans', sans-serif; font-size: 10px; font-weight: 700; border-radius: 5px; padding: 2px 6px; flex: 0 0 auto; }
.gx-badge.re { background: var(--re-bg); color: var(--re-fg); }
.gx-badge.rb { background: var(--rb-bg); color: var(--rb-fg); }
.gx-badge.s  { background: var(--s-bg);  color: var(--s-fg); }
.gx-badge.other { background: var(--surf3); color: var(--muted); }
.gx-name { flex: 1; min-width: 0; font-family: 'Fraunces', serif; font-size: 14px; font-weight: 700; color: var(--ink); letter-spacing: -.02em; line-height: 1.1; text-transform: uppercase; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.gx-chev { color: var(--faint); font-size: 13px; flex: 0 0 auto; }
.gx-meta { display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-top: 7px; font-size: 12px; }
.gx-meta-time { color: var(--ink); font-weight: 600; font-variant-numeric: tabular-nums; }
.gx-expand { background: none; border: none; padding: 0; font-family: 'DM Sans', sans-serif; font-size: 11px; font-weight: 500; color: var(--accent); cursor: pointer; }
.gx-expand:hover { text-decoration: underline; }

.gx-inter { margin-top: 9px; border-top: 1px solid var(--bdr); padding-top: 8px; }
.gx-inter-row { display: flex; justify-content: space-between; gap: 10px; padding: 4px 2px; font-size: 12px; color: var(--muted); cursor: pointer; border-radius: 6px; }
.gx-inter-row:hover { color: var(--ink); background: var(--surf2); }
.gx-inter-row .nm { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.gx-inter-row .tm { flex: 0 0 auto; font-variant-numeric: tabular-nums; }

.gx-msg { font-size: 13px; color: var(--muted); }
.gx-msg.err { color: var(--accent); }
`;

// Fit the map to all currently visible pins whenever they change.
function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points.length) return;
    if (points.length === 1) { map.setView(points[0], 11); return; }
    map.fitBounds(points, { padding: [40, 40] });
  }, [JSON.stringify(points)]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// Gently pan to a target [lat,lon] (mobile: follow the active carousel card).
function FlyTo({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.panTo(target, { animate: true, duration: 0.4 });
  }, [target ? target.join(',') : null]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// ≤640px → mobile layout (top trip bar + bottom-sheet carousel)
function useIsMobile() {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia('(max-width: 640px)').matches);
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    const fn = () => setM(mq.matches);
    mq.addEventListener ? mq.addEventListener('change', fn) : mq.addListener(fn);
    return () => { mq.removeEventListener ? mq.removeEventListener('change', fn) : mq.removeListener(fn); };
  }, []);
  return m;
}

export default function GTFSExplorer({ originStation, apiFetch, API_BASE, onModeChange, lang = 'en', setLang = () => {} }) {
  const fetcher = apiFetch || defaultApiFetch;
  const base = API_BASE || DEFAULT_API_BASE;

  const [origin, setOrigin] = useState({ name: originStation.name, lat: originStation.lat, lon: originStation.lng });
  const [query, setQuery] = useState(originStation.name);
  const [path, setPath] = useState([]);            // [{ name, lat, lon, route, legSeconds }]
  const [cache, setCache] = useState({});          // name -> { loading, error, routes }
  const [filters, setFilters] = useState({ RE: true, RB: false, S: false });
  const [showInter, setShowInter] = useState(false);
  const [openDir, setOpenDir] = useState(null);    // "routeId|dirIndex" of expanded card
  const [hoveredKey, setHoveredKey] = useState(null); // "routeId|dirIndex" hovered (card or pin)
  const [suggestions, setSuggestions] = useState([]); // station search dropdown
  const [showSuggest, setShowSuggest] = useState(false);
  const [activeCard, setActiveCard] = useState(0);      // mobile carousel active index
  const [tripOpen, setTripOpen] = useState(false);      // "Your trip" accordion open (desktop + mobile)
  const [showPlan, setShowPlan] = useState(false);      // full-screen "Plan this trip" view
  const [editing, setEditing] = useState(false);        // mobile origin search open
  const [dep, setDep] = useState({});                   // stop_name -> { loading, error, trains }
  const [showChai, setShowChai] = useState(false);
  const [chaiShown, setChaiShown] = useState(false);
  const interactionCount = useRef(0);
  const cacheRef = useRef({});
  const depRef = useRef({});
  const searchRef = useRef(null);
  const cardRefs = useRef({}); // key -> card DOM node, for scroll-into-view on pin hover
  const carouselRef = useRef(null);
  const cardElsRef = useRef(new Map()); // key -> mobile carousel card el (for IntersectionObserver)
  const isMobile = useIsMobile();

  // bring the highlighted card into view (e.g. when hover originates from a map pin)
  useEffect(() => {
    if (hoveredKey && cardRefs.current[hoveredKey]) {
      cardRefs.current[hoveredKey].scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }
  }, [hoveredKey]);

  // debounced station-name search for the dropdown
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setSuggestions([]); return; }
    const id = setTimeout(() => {
      fetcher(`${base}/stations?q=${encodeURIComponent(q)}`)
        .then((r) => r.json())
        .then((d) => setSuggestions((d.stations || []).slice(0, 8)))
        .catch(() => setSuggestions([]));
    }, 220);
    return () => clearTimeout(id);
  }, [query, base, fetcher]);

  // ── connections grouped by direction (hops=0), cached by stop name ──
  const fetchConnections = useCallback(async (name) => {
    if (cacheRef.current[name]) return;
    cacheRef.current = { ...cacheRef.current, [name]: { loading: true, error: null, routes: [] } };
    setCache(cacheRef.current);
    try {
      const r = await fetcher(`${base}/connections?stop_name=${encodeURIComponent(name)}&hops=0`);
      const d = await r.json();
      cacheRef.current = { ...cacheRef.current, [name]: { loading: false, error: null, routes: d.routes || [] } };
    } catch (e) {
      cacheRef.current = { ...cacheRef.current, [name]: { loading: false, error: e.message || 'Failed', routes: [] } };
    }
    setCache(cacheRef.current);
  }, [base, fetcher]);

  // ── next departures for a stop (lazy, for the trip timeline) ──
  const fetchDepartures = useCallback(async (name) => {
    if (depRef.current[name]) return;
    depRef.current = { ...depRef.current, [name]: { loading: true, error: null, trains: [] } };
    setDep(depRef.current);
    try {
      const sr = await fetcher(`${base}/stations?q=${encodeURIComponent(name)}`);
      const sd = await sr.json();
      const eva = sd.stations?.[0]?.eva;
      if (!eva) throw new Error('not found');
      const tr = await fetcher(`${base}/timetable?eva=${eva}&offset=0`);
      const td = await tr.json();
      const trains = (td?.timetable?.s || [])
        .filter((x) => x.dp)
        .map((x) => ({
          id: x.id,
          line: (x.dp.l || x.dp.fb) || `${x.tl?.c || ''} ${x.tl?.n || ''}`.trim(),
          time: fmtTime(x.dp.pt),
          dest: x.dp.ppth ? x.dp.ppth.split('|').pop() : '—',
        }))
        .sort((a, b) => a.time.localeCompare(b.time))
        .slice(0, 3);
      depRef.current = { ...depRef.current, [name]: { loading: false, error: null, trains } };
    } catch {
      depRef.current = { ...depRef.current, [name]: { loading: false, error: 'err', trains: [] } };
    }
    setDep(depRef.current);
  }, [base, fetcher]);

  // ── load a station as the new origin (coords from props on mount, else geocode) ──
  const loadStation = useCallback(async (name, coords) => {
    name = (name || '').trim();
    if (!name) return;
    setPath([]);
    setOpenDir(null);
    const ll = coords && coords[0] != null && coords[1] != null ? coords : null;
    setOrigin({ name, lat: ll ? ll[0] : null, lon: ll ? ll[1] : null });
    if (!ll) {
      try {
        const r = await fetcher(`${base}/geocode?names=${encodeURIComponent(name)}`);
        const g = await r.json();
        const c = g && g[name];
        if (c) setOrigin((o) => (o.name === name ? { ...o, lat: c[0], lon: c[1] } : o));
      } catch { /* ignore */ }
    }
    fetchConnections(name);
  }, [base, fetcher, fetchConnections]);

  useEffect(() => {
    setQuery(originStation.name);
    loadStation(originStation.name, [originStation.lat, originStation.lng]);
  }, [originStation.name]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── navigation ──
  // coords of the node we're currently exploring from (origin, or last path node)
  const currentLL = () => {
    if (path.length) { const n = path[path.length - 1]; return (n.lat != null && n.lon != null) ? [n.lat, n.lon] : null; }
    return (origin.lat != null && origin.lon != null) ? [origin.lat, origin.lon] : null;
  };
  // nudge a "buy me a chai" popup after a handful of interactions
  const trackInteraction = () => {
    if (chaiShown) return;
    interactionCount.current += 1;
    if (interactionCount.current >= 6) {
      setShowChai(true);
      setChaiShown(true);
    }
  };
  const selectStop = (node) => {
    setPath((p) => [...p, node]);
    setOpenDir(null);
    if (node.name) fetchConnections(node.name);
    trackInteraction();
  };
  // drill into a direction's terminal — leg geometry = origin → intermediates → terminal
  const drillTerminal = (route, dir) => {
    const start = currentLL();
    const legCoords = start ? [start] : [];
    (dir.intermediate_stops || []).forEach((s) => { const ll = llOf(s); if (ll) legCoords.push(ll); });
    const tll = llOf(dir.terminal); if (tll) legCoords.push(tll);
    selectStop({
      name: dir.terminal.stop_name, lat: dir.terminal.stop_lat, lon: dir.terminal.stop_lon,
      route: route.route_short_name, legSeconds: dir.travel_time_seconds, legCoords,
    });
  };
  // drill into one intermediate stop — leg geometry = origin → intermediates up to it
  const drillIntermediate = (route, dir, idx) => {
    const start = currentLL();
    const legCoords = start ? [start] : [];
    const upto = (dir.intermediate_stops || []).slice(0, idx + 1);
    upto.forEach((s) => { const ll = llOf(s); if (ll) legCoords.push(ll); });
    const s = upto[upto.length - 1];
    selectStop({
      name: s.stop_name, lat: s.stop_lat, lon: s.stop_lon,
      route: route.route_short_name, legSeconds: s.travel_time_seconds, legCoords,
    });
  };
  const removeLast = () => { setPath((p) => p.slice(0, -1)); setOpenDir(null); };
  // pick a station from the dropdown — loads it as the new origin
  const pickStation = (s) => {
    setQuery(s.name);
    setShowSuggest(false);
    setEditing(false);
    loadStation(s.name);
    trackInteraction();
  };
  // origin clicked / search reset → clear path, focus input, reopen the dropdown
  const resetToSearch = () => {
    setPath([]);
    setOpenDir(null);
    setShowSuggest(true);
    if (searchRef.current) { searchRef.current.focus(); searchRef.current.select(); }
  };
  // mobile trip-pill tapped at origin level → edit the starting station
  const editOrigin = () => {
    setEditing(true);
    setQuery('');
    setShowSuggest(true);
    setTimeout(() => { if (searchRef.current) searchRef.current.focus(); }, 0);
  };
  const toggleFilter = (k) => setFilters((f) => ({ ...f, [k]: !f[k] }));
  const toggleDir = (key) => setOpenDir((cur) => (cur === key ? null : key));
  // open the full-screen "Plan this trip" result view
  const planTrip = () => { trackInteraction(); setShowPlan(true); };


  // ── derived ──
  const depth = path.length;
  const currentName = depth ? path[depth - 1].name : origin.name;
  const entry = cache[currentName] || { loading: true, error: null, routes: [] };
  const routes = (entry.routes || []).filter((r) => matchesFilters(r.route_short_name, filters));
  const curColor = colorAt(depth);
  const lightColor = lightAt(depth);

  // flatten to one entry per direction (= one card / one terminal pin)
  const dirCards = routes.flatMap((r) =>
    (r.directions || []).map((dir, di) => ({ key: `${r.route_id}|${di}`, route: r, dir })));
  const activeIdx = Math.min(activeCard, Math.max(0, dirCards.length - 1)); // mobile carousel
  const dirKeys = dirCards.map((c) => c.key).join(',');

  // reset the mobile carousel + close the trip accordion when the layer changes
  useEffect(() => {
    setActiveCard(0);
    setHoveredKey(null);
    setTripOpen(false);
    if (carouselRef.current) carouselRef.current.scrollLeft = 0;
  }, [currentName]);

  // lazy-load departures for every stop in the trip when the accordion opens
  useEffect(() => {
    if (!tripOpen) return;
    [origin.name, ...path.map((n) => n.name)].forEach((nm) => { if (nm && !depRef.current[nm]) fetchDepartures(nm); });
  }, [tripOpen, depth]); // eslint-disable-line react-hooks/exhaustive-deps

  // mobile: observe which card is in view → drive activeCard + hoveredKey (map sync)
  useEffect(() => {
    if (!isMobile) return undefined;
    const root = carouselRef.current;
    if (!root) return undefined;
    const obs = new IntersectionObserver((entries) => {
      let best = null;
      for (const e of entries) {
        if (e.isIntersecting && (!best || e.intersectionRatio > best.intersectionRatio)) best = e;
      }
      if (best && best.intersectionRatio >= 0.6) {
        setActiveCard(Number(best.target.dataset.idx));
        setHoveredKey(best.target.dataset.key);
      }
    }, { root, threshold: [0.5, 0.75, 1] });
    cardElsRef.current.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, [isMobile, dirKeys]);

  const originLL = (origin.lat != null && origin.lon != null) ? [origin.lat, origin.lon] : null;
  const pathLLs = path.filter((n) => n.lat != null && n.lon != null).map((n) => [n.lat, n.lon]);
  const termPins = dirCards.map((c) => ({ ...c, ll: llOf(c.dir.terminal) })).filter((c) => c.ll);
  const activeKey = dirCards[activeIdx] ? dirCards[activeIdx].key : null;
  const activeTermLL = (termPins.find((c) => c.key === activeKey) || {}).ll || null;
  const interPins = showInter
    ? dirCards.flatMap(({ route, dir }) =>
        (dir.intermediate_stops || [])
          .map((s, idx) => ({ route, dir, idx, s, ll: llOf(s) }))
          .filter((x) => x.ll))
    : [];

  // node we explore from (for current-layer route lines)
  const curNodeLL = path.length
    ? ((path[path.length - 1].lat != null && path[path.length - 1].lon != null)
        ? [path[path.length - 1].lat, path[path.length - 1].lon] : null)
    : originLL;

  // bold selected path through every leg's full geometry (origin → … → last)
  const selectedPath = originLL ? [originLL] : [];
  path.forEach((n) => {
    if (n.legCoords && n.legCoords.length > 1) {
      n.legCoords.slice(1).forEach((c) => selectedPath.push(c));
    } else if (n.lat != null && n.lon != null) {
      selectedPath.push([n.lat, n.lon]);
    }
  });

  // current-layer route line: origin → intermediates → terminal
  const linePositions = (dir) => {
    if (!curNodeLL) return null;
    const pts = [curNodeLL];
    (dir.intermediate_stops || []).forEach((s) => { const ll = llOf(s); if (ll) pts.push(ll); });
    const tll = llOf(dir.terminal); if (tll) pts.push(tll);
    return pts.length >= 2 ? pts : null;
  };

  const points = (originLL ? [originLL] : [])
    .concat(pathLLs)
    .concat(termPins.map((c) => c.ll))
    .concat(interPins.map((x) => x.ll));
  const center = originLL || [49.6, 11.0];

  // condensed path text for the trip header, e.g. "Erlangen → Nürnberg → … → Garmisch"
  const tripNodeNames = [origin.name, ...path.map((n) => n.name)];
  const condensedPath = tripNodeNames.length <= 4
    ? tripNodeNames.join(' → ')
    : `${tripNodeNames[0]} → ${tripNodeNames[1]} → … → ${tripNodeNames[tripNodeNames.length - 1]}`;

  // departures line for one stop
  const renderDep = (name) => {
    const d = dep[name];
    if (!d || d.loading) return <span className="gx-tl-dep load">loading…</span>;
    if (d.error || !d.trains.length) return <span className="gx-tl-dep none">no departures</span>;
    return <span className="gx-tl-dep">Next: {d.trains.map((t) => t.time).join(' · ')}</span>;
  };

  // shared timeline: each stop with leg badge + travel time + next departures
  const renderTimeline = () => {
    const nodes = [{ name: origin.name, route: null, legSeconds: null }, ...path];
    return (
      <div className="gx-tl">
        {nodes.map((n, i) => (
          <div className="gx-tl-node" key={`${n.name}-${i}`}>
            {i > 0 && (
              <div className="gx-tl-leg">
                <span className={`gx-badge ${catOf(n.route)}`}>{n.route || '?'}</span>
                <span className="gx-tl-legtime">{mins(n.legSeconds) != null ? `${mins(n.legSeconds)} min` : ''}</span>
              </div>
            )}
            <div className="gx-tl-stop">
              <span className="gx-tl-dot" style={{ background: i === 0 ? '#1A1A1A' : colorAt(i - 1) }} />
              <span className="gx-tl-name">{n.name}</span>
            </div>
            <div className="gx-tl-deprow">{renderDep(n.name)}</div>
          </div>
        ))}
        <button className="gx-tl-remove" onClick={removeLast}>× Remove last stop</button>
      </div>
    );
  };

  if (showPlan && depth >= 1) {
    return (
      <TripPlan
        path={path}
        originStation={origin.name}
        onBack={() => setShowPlan(false)}
        apiFetch={fetcher}
        API_BASE={base} />
    );
  }

  return (
    <div className="gx-root">
      <style>{STYLES}</style>

      {/* full-screen map */}
      <div className="gx-map">
        <MapContainer center={center} zoom={9} style={{ height: '100%', width: '100%' }}>
          <TileLayer
            url={`https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
            subdomains="abcd" maxZoom={20} zIndex={1}
            attribution="&copy; OpenStreetMap &copy; CARTO" />
          <TileLayer
            url={`https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png?key=${CARTO_KEY}`}
            subdomains="abcd" maxZoom={20} zIndex={2} />

          <FitBounds points={points} />
          {isMobile && <FlyTo target={activeTermLL} />}

          {/* current-layer route lines: origin → intermediates → terminal */}
          {dirCards.map(({ key, dir }) => {
            const pos = linePositions(dir);
            if (!pos) return null;
            const hov = hoveredKey === key;
            return (
              <Polyline key={`rl-${key}`} positions={pos}
                pathOptions={hov
                  ? { color: ROUTE_COLOR, opacity: 0.7, weight: 2.5 }
                  : { color: '#1C1C1A', opacity: 0.25, weight: 1.5 }} />
            );
          })}

          {/* bold selected path once drilled in */}
          {selectedPath.length >= 2 && (
            <Polyline positions={selectedPath} pathOptions={{ color: ROUTE_COLOR, opacity: 0.9, weight: 4 }} />
          )}

          {/* intermediate stops (lighter, clickable, when enabled) */}
          {interPins.map((x, i) => (
            <Marker key={`i-${x.s.stop_name}-${i}`} position={x.ll} icon={makeIcon(lightColor, 8)}
              eventHandlers={{ click: () => drillIntermediate(x.route, x.dir, x.idx) }}>
              <Popup>{x.s.stop_name}{x.s.travel_time_seconds != null ? ` · ${mins(x.s.travel_time_seconds)} min` : ''}</Popup>
            </Marker>
          ))}

          {/* origin (dark) */}
          {originLL && (
            <Marker position={originLL} icon={makeIcon('#1A1A1A', 20)}>
              <Popup>{origin.name}</Popup>
            </Marker>
          )}

          {/* selected path nodes (depth colour) */}
          {path.map((n, i) => (
            (n.lat != null && n.lon != null) && (
              <Marker key={`p-${n.name}-${i}`} position={[n.lat, n.lon]} icon={makeIcon(colorAt(i), 14)}>
                <Popup>{n.name}{n.route ? ` (${n.route})` : ''}</Popup>
              </Marker>
            )
          ))}

          {/* terminal pins — active/hovered one is larger + coral */}
          {termPins.map((c) => (
            <Marker key={`t-${c.key}`} position={c.ll}
              icon={hoveredKey === c.key ? makeIcon(ROUTE_COLOR, 18) : makeIcon(curColor, 14)}
              eventHandlers={{
                click: () => drillTerminal(c.route, c.dir),
                mouseover: () => setHoveredKey(c.key),
                mouseout: () => setHoveredKey(null),
              }}>
              <Popup>{c.route.route_short_name} → {c.dir.terminal.stop_name}{c.dir.travel_time_seconds != null ? ` · ${mins(c.dir.travel_time_seconds)} min` : ''}</Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>

      {/* map dim while the trip accordion is open (tap to close) */}
      {tripOpen && depth >= 1 && <div className="gx-dim" onClick={() => setTripOpen(false)} />}

      {/* desktop floating left panel */}
      {!isMobile && (
      <div className="gx-panel">
        <div className="gx-nav">
          <a className="gx-logo" href="/" style={{ textDecoration: 'none' }}>zwischen</a>
          <nav className="gx-nav-links">
            <button className="gx-nav-link">About</button>
            <a className="gx-nav-link" href="#">Privacy</a>
            <a className="gx-nav-link" href="https://ko-fi.com/adityaaserkar" target="_blank" rel="noreferrer">☕ Support</a>
            <button
              className="gx-lang-btn"
              onClick={() => setLang((l) => (l === 'en' ? 'de' : 'en'))}
              style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, border: '0.5px solid var(--bdr2)', background: 'var(--surface)', cursor: 'pointer', color: 'var(--muted)' }}
            >
              {lang === 'en' ? 'DE' : 'EN'}
            </button>
          </nav>
        </div>

        <div className="gx-modes">
          {MODES.map((m) => (
            <div key={m.id} className={`gx-mode-card${m.active ? ' active' : ''}${m.soon ? ' soon' : ''}`}
              onClick={() => { if (!m.active && !m.soon && onModeChange) onModeChange(m.id); }}>
              <span className="gx-mode-icon">{m.icon}</span>
              <span className="gx-mode-text">
                <span className="gx-mode-label">{m.label}</span>
                <span className="gx-mode-desc">{m.desc}</span>
              </span>
            </div>
          ))}
        </div>

        <div className="gx-search-wrap">
          <input
            ref={searchRef}
            className="gx-search"
            placeholder="e.g. München Hbf"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
            onFocus={() => setShowSuggest(true)}
            onBlur={() => setTimeout(() => setShowSuggest(false), 120)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setShowSuggest(false); loadStation(e.target.value); trackInteraction(); } }}
            autoComplete="off" />
          {showSuggest && suggestions.length > 0 && (
            <div className="gx-suggest">
              {suggestions.map((s) => (
                <button key={s.eva || s.name} className="gx-suggest-item"
                  onMouseDown={(e) => { e.preventDefault(); pickStation(s); }}>
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>

        {depth === 0 && (
          <p className="gx-desc">
            Discover where your Deutschland-Ticket takes you. Pick a station and explore every RE, RB and S-Bahn connection — tap any destination to keep going.
          </p>
        )}

        <div className="gx-pills">
          {['RE', 'RB', 'S'].map((k) => (
            <button key={k} className={`gx-pill ${k}${filters[k] ? ' active' : ''}`} onClick={() => toggleFilter(k)}>
              {k}
            </button>
          ))}
        </div>

        <label className="gx-check">
          <input type="checkbox" checked={showInter} onChange={(e) => setShowInter(e.target.checked)} />
          Show intermediate stops
        </label>

        {/* "Your trip" section */}
        {depth >= 1 && (
          <div className="gx-trip">
            <button className="gx-trip-head" onClick={() => setTripOpen((v) => !v)}>
              <span className="gx-trip-label">Your trip</span>
              <span className="gx-trip-condensed">{condensedPath}</span>
              <span className="gx-trip-chev">{tripOpen ? '▴' : '▾'}</span>
            </button>
            <div className="gx-trip-cta">
              <span className="gx-trip-cta-txt">Happy with this?</span>
              <button className="gx-plan-btn" onClick={planTrip}>Plan this trip →</button>
            </div>
            <div className={`gx-trip-acc${tripOpen ? ' open' : ''}`}>
              <div className="gx-trip-acc-inner">{renderTimeline()}</div>
            </div>
          </div>
        )}

        {/* direction cards / messages */}
        {entry.loading && <div className="gx-msg">Loading connections…</div>}
        {entry.error && <div className="gx-msg err">Error: {entry.error}</div>}
        {!entry.loading && !entry.error && dirCards.length === 0 && (
          <div className="gx-msg">No connections match the selected filters.</div>
        )}
        {!entry.loading && !entry.error && dirCards.length > 0 && (
          <div>
            <div className="gx-count">{dirCards.length} direction{dirCards.length !== 1 ? 's' : ''}</div>
            {dirCards.map(({ key, route, dir }) => {
              const open = openDir === key;
              const inter = dir.intermediate_stops || [];
              const m = mins(dir.travel_time_seconds);
              return (
                <motion.div key={key}
                  ref={(el) => { if (el) cardRefs.current[key] = el; else delete cardRefs.current[key]; }}
                  className={`gx-card${open ? ' open' : ''}${hoveredKey === key ? ' gx-card--highlighted' : ''}`}
                  whileHover={{ scale: 1.015, boxShadow: '0 6px 24px rgba(232,99,74,0.15)' }}
                  transition={{ duration: 0.15, ease: 'easeOut' }}
                  onMouseEnter={() => setHoveredKey(key)} onMouseLeave={() => setHoveredKey(null)}>
                  <div className="gx-row" onClick={() => drillTerminal(route, dir)}>
                    <span className="gx-dot" style={{ background: curColor }} />
                    <span className={`gx-badge ${catOf(route.route_short_name)}`}>{route.route_short_name}</span>
                    <span className="gx-name">{dir.terminal.stop_name}</span>
                    <span className="gx-chev">›</span>
                  </div>
                  <div className="gx-meta">
                    <span className="gx-meta-time">{m != null ? `${m} min` : '—'}</span>
                    {inter.length > 0 && (
                      <button className="gx-expand" onClick={(e) => { e.stopPropagation(); toggleDir(key); }}>
                        {open ? 'Hide stops ▴' : `${inter.length} stop${inter.length !== 1 ? 's' : ''} ▾`}
                      </button>
                    )}
                  </div>
                  {open && inter.length > 0 && (
                    <div className="gx-inter">
                      {inter.map((s, si) => (
                        <div className="gx-inter-row" key={`${s.stop_name}-${si}`}
                          onClick={(e) => { e.stopPropagation(); drillIntermediate(route, dir, si); }}>
                          <span className="nm">{s.stop_name}</span>
                          <span className="tm">{s.travel_time_seconds != null ? `${mins(s.travel_time_seconds)} min` : ''}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
      )}

      {/* mobile: floating top trip bar + bottom-sheet carousel */}
      {isMobile && (
        <>
          <div className="gxm-top">
            <button className="gxm-back" aria-label="Back"
              onClick={() => { if (editing) setEditing(false); else if (depth >= 1) removeLast(); }}>‹</button>
            <div className="gxm-pill">
              {editing ? (
                <div className="gxm-search-wrap">
                  <input ref={searchRef} className="gxm-search" autoFocus value={query}
                    placeholder="Search a station…"
                    onChange={(e) => { setQuery(e.target.value); setShowSuggest(true); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { setShowSuggest(false); setEditing(false); loadStation(e.target.value); trackInteraction(); } }} />
                  {showSuggest && suggestions.length > 0 && (
                    <div className="gxm-suggest">
                      {suggestions.map((s) => (
                        <button key={s.eva || s.name} className="gx-suggest-item"
                          onMouseDown={(e) => { e.preventDefault(); pickStation(s); }}>{s.name}</button>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <>
                  <button className="gxm-pill-head"
                    onClick={() => { if (depth >= 1) setTripOpen((v) => !v); else editOrigin(); }}>
                    <span className="gxm-origin-dot" />
                    <span className="gxm-path">{condensedPath}</span>
                    {depth >= 1
                      ? <span className="gxm-chev">{tripOpen ? '▴' : '▾'}</span>
                      : <span className="gxm-edit">Edit ›</span>}
                  </button>
                  {depth >= 1 && (
                    <div className={`gx-trip-acc${tripOpen ? ' open' : ''}`}>
                      <div className="gx-trip-acc-inner gxm-acc-inner">
                        <button className="gxm-acc-x" aria-label="Close" onClick={() => setTripOpen(false)}>×</button>
                        {renderTimeline()}
                        <button className="gx-plan-btn gxm-plan" onClick={planTrip}>Plan this trip →</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>

          <div className="gxm-sheet">
            <div className="gxm-handle"><span className="gxm-handle-pill" /></div>
            <div className="gxm-count">
              {depth >= 1 ? `From ${currentName} · ` : ''}{dirCards.length} direction{dirCards.length !== 1 ? 's' : ''}
            </div>

            {entry.loading && <div className="gx-msg" style={{ padding: '0 12px 10px' }}>Loading connections…</div>}
            {entry.error && <div className="gx-msg err" style={{ padding: '0 12px 10px' }}>Error: {entry.error}</div>}
            {!entry.loading && !entry.error && dirCards.length === 0 && (
              <div className="gx-msg" style={{ padding: '0 12px 10px' }}>No connections match the selected filters.</div>
            )}
            {!entry.loading && !entry.error && dirCards.length > 0 && (
              <>
                <div className="gxm-carousel" ref={carouselRef}>
                  {dirCards.map(({ key, route, dir }, i) => {
                    const open = openDir === key;
                    const inter = dir.intermediate_stops || [];
                    return (
                      <div key={key} data-key={key} data-idx={i}
                        ref={(el) => { if (el) cardElsRef.current.set(key, el); else cardElsRef.current.delete(key); }}
                        className={`gxm-card${i === activeIdx ? ' active' : ''}`}>
                        <div className="gxm-card-name" onClick={() => drillTerminal(route, dir)}>{dir.terminal.stop_name}</div>
                        <div className="gxm-card-row">
                          <span className="gxm-card-meta">{mins(dir.travel_time_seconds)} min · {route.route_short_name}</span>
                          {inter.length > 0 && (
                            <span className="gxm-card-link" onClick={() => toggleDir(key)}>
                              {open ? 'Hide stops' : `Show ${inter.length} stop${inter.length !== 1 ? 's' : ''}`}
                            </span>
                          )}
                        </div>
                        {open && inter.length > 0 && (
                          <div className="gxm-card-inter">
                            {inter.map((s, si) => (
                              <div key={`${s.stop_name}-${si}`} className="gxm-card-inter-row"
                                onClick={() => drillIntermediate(route, dir, si)}>
                                <span>{s.stop_name}</span>
                                <span>{s.travel_time_seconds != null ? `${mins(s.travel_time_seconds)} min` : ''}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
                <div className="gxm-dots">
                  {dirCards.map((c, i) => (<span key={i} className={`gxm-dot${i === activeIdx ? ' active' : ''}`} />))}
                </div>
              </>
            )}

            <div className="gxm-filters">
              {['RE', 'RB', 'S'].map((k) => (
                <button key={k} className={`gx-pill ${k}${filters[k] ? ' active' : ''}`} onClick={() => toggleFilter(k)}>{k}</button>
              ))}
              <label className="gxm-check">
                <input type="checkbox" checked={showInter} onChange={(e) => setShowInter(e.target.checked)} />
                Intermediate
              </label>
            </div>
          </div>
        </>
      )}

      {showChai && (
        <div style={{
          position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
          zIndex: 9999, background: 'var(--surface)',
          border: '0.5px solid var(--bdr)',
          borderRadius: 16, padding: '16px 20px', boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
          maxWidth: 320, width: 'calc(100vw - 48px)', textAlign: 'center'
        }}>
          <div style={{ fontSize: 24, marginBottom: 6 }}>☕</div>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--ink)', marginBottom: 4 }}>
            Enjoying zwischen?
          </div>
          <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 14, lineHeight: 1.4 }}>
            It's free and ad-free. If it's useful, help keep it that way.
          </div>
          <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
            <button
              onClick={() => setShowChai(false)}
              style={{ fontSize: 12, padding: '7px 16px', borderRadius: 20, border: '0.5px solid var(--bdr2)', background: 'var(--surf2)', cursor: 'pointer', color: 'var(--muted)' }}
            >
              Maybe later
            </button>
            <a
              href="https://ko-fi.com/adityaaserkar"
              target="_blank"
              rel="noreferrer"
              onClick={() => setShowChai(false)}
              style={{ fontSize: 12, padding: '7px 16px', borderRadius: 20, background: '#E8634A', color: 'white', textDecoration: 'none', fontWeight: 500 }}
            >
              ☕ Buy me a chai →
            </a>
          </div>
        </div>
      )}
    </div>
  );
}
